var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import SwitchUnstyled from '@mui/base/Switch';
import { styled, Box } from '@mui/material';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { blue05, blue10, blue20, blue40, blue50, blue60 } from '../../colors/blue';
import { gray40, gray40opacity20, gray40opacity40, gray50, white } from '../../colors/gray';
import { neutral60opacity10, neutral60opacity20 } from '../../colors/neutral';
import { red02, red03 } from '../../colors/red';
import { isLightTheme } from '../../utils/utils';
import { rem } from '../../utils/rem';
var Root = styled(Box)(function (_a) {
    var theme = _a.theme;
    var lightTheme = isLightTheme(theme);
    return {
        alignItems: 'center',
        borderRadius: rem(9),
        display: 'inline-flex',
        height: rem(18),
        justifyContent: 'center',
        marginInlineStart: rem(11),
        marginInlineEnd: rem(8),
        position: 'relative',
        width: rem(30),
        '&:hover .MuiSwitch-track': {
            background: lightTheme ? gray40opacity40 : neutral60opacity20,
        },
        '&.Mui-checked': {
            '& .MuiSwitch-track': {
                background: lightTheme ? blue50 : blue20,
                borderColor: lightTheme ? blue50 : blue20,
            },
            '& .MuiSwitch-thumb': {
                background: lightTheme ? white : blue60,
                borderColor: lightTheme ? blue40 : blue60,
                height: rem(12),
                transform: lightTheme ? "translateX(".concat(rem(12), ")") : "translateX(".concat(rem(11), ")"),
                transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                width: rem(12),
            },
            '&:hover .MuiSwitch-track': {
                background: lightTheme ? blue60 : blue10,
                borderColor: lightTheme ? blue60 : blue10,
            },
            '&.Mui-error .MuiSwitch-track': {
                background: lightTheme ? red03 : red02,
                borderColor: lightTheme ? red03 : red02,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-input': {
            boxShadow: lightTheme ? "0 0 0 ".concat(rem(2), " ").concat(blue60) : "0 0 0 ".concat(rem(2), " ").concat(blue05),
        },
        '&.Mui-error': {
            '& .MuiSwitch-track': {
                borderColor: lightTheme ? red03 : red02,
            },
            '& .MuiSwitch-thumb': {
                borderColor: lightTheme ? red03 : 'transparent',
            },
        },
        '&.Mui-disabled': {
            opacity: 0.4,
            pointerEvents: 'none',
        },
    };
});
var Track = styled('span')(function (_a) {
    var theme = _a.theme;
    var lightTheme = isLightTheme(theme);
    return {
        background: lightTheme ? gray40opacity20 : neutral60opacity10,
        borderColor: lightTheme ? gray40 : gray50,
        borderRadius: rem(8),
        borderStyle: 'solid',
        borderWidth: rem(1),
        height: rem(16),
        pointerEvents: 'none',
        position: 'absolute',
        width: rem(28),
    };
});
var Thumb = styled('span')(function (_a) {
    var theme = _a.theme;
    var lightTheme = isLightTheme(theme);
    return {
        background: lightTheme ? white : gray50,
        borderColor: lightTheme ? gray40 : 'transparent',
        borderRadius: '50%',
        borderStyle: 'solid',
        borderWidth: rem(1),
        height: lightTheme ? rem(12) : rem(10),
        left: lightTheme ? rem(3) : rem(4),
        pointerEvents: 'none',
        position: 'absolute',
        transform: 'translateX(0px)',
        transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        width: lightTheme ? rem(12) : rem(10),
    };
});
var Input = styled('input')({
    appearance: 'none',
    borderRadius: rem(9),
    cursor: 'pointer',
    height: '100%',
    margin: 0,
    outline: 'none',
    width: '100%',
    minWidth: rem(30),
});
var Switch = forwardRef(function (_a, ref) {
    var error = _a.error, className = _a.className, inputProps = _a.inputProps, props = __rest(_a, ["error", "className", "inputProps"]);
    return (React.createElement(SwitchUnstyled, __assign({ slots: { root: Root, track: Track, thumb: Thumb, input: Input }, slotProps: __assign({ input: inputProps }, props.slotProps), className: clsx(error && 'Mui-error', className) }, __assign({ ref: ref }, props))));
});
Switch.displayName = 'Switch';
export default Switch;
